import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Section } from '@dized/ui';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import TooltipOverlay from './TooltipOverlay';
import { 
  isWeekend, 
  validateClickData, 
  prepareTooltipCountryData
} from './chartHelpers';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ResponsiveTitle = styled.h3`
  font-size: clamp(1.25rem, 3vw, 1.5rem);
  margin: 0 0 20px 0;
  word-break: break-word;
  line-height: 1.3;
  
  @media (max-width: 480px) {
    margin: 0 0 15px 0;
  }
`;

const ChartWrapper = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  position: relative;
`;

const ChartContainer = styled.div`
  height: 400px;
  width: 100%;
  position: relative;
`;

const DailyStats = ({ data, isLoading, error }) => {
  const [extendedTooltip, setExtendedTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isMouseTracking, setIsMouseTracking] = useState(false);
  const [fixedHorizontalPosition, setFixedHorizontalPosition] = useState(null);
  const [lastHoveredIndex, setLastHoveredIndex] = useState(null);
  const chartRef = useRef(null);

  // Update tooltip position based on mouse movement
  const updateTooltipPosition = useCallback((clientX, clientY) => {
    if (!isMouseTracking || !fixedHorizontalPosition) return;
    
    const screenWidth = window.innerWidth;
    const tooltipWidth = screenWidth > 1200 ? 700 : screenWidth > 768 ? 500 : 350;
    
    // Use fixed horizontal position from data point, only track vertical
    let left = fixedHorizontalPosition;
    let top = clientY + 30; // Always position below the cursor with consistent offset
    
    // Only adjust horizontal position if tooltip would go off screen
    if (left + tooltipWidth > screenWidth) {
      left = screenWidth - tooltipWidth - 10;
    }
    if (left < 10) {
      left = 10;
    }
    
    // Keep tooltip below cursor, let user scroll if needed
    // This provides consistent UX without jumping
    
    setTooltipPosition({ top, left });
  }, [isMouseTracking, fixedHorizontalPosition]);

  // Mouse move handler for cursor following
  const handleMouseMove = useCallback((event) => {
    if (extendedTooltip && isMouseTracking) {
      // Check if mouse is over the tooltip overlay to pause tracking
      const tooltipElement = document.querySelector('[data-tooltip-overlay]');
      if (tooltipElement) {
        const rect = tooltipElement.getBoundingClientRect();
        const isOverTooltip = (
          event.clientX >= rect.left && 
          event.clientX <= rect.right &&
          event.clientY >= rect.top && 
          event.clientY <= rect.bottom
        );
        
        // Only update position if not hovering over tooltip
        if (!isOverTooltip) {
          updateTooltipPosition(event.clientX, event.clientY);
        }
      } else {
        updateTooltipPosition(event.clientX, event.clientY);
      }
    }
  }, [extendedTooltip, isMouseTracking, updateTooltipPosition]);

  // Add global mouse move listener when tracking is enabled
  useEffect(() => {
    if (isMouseTracking) {
      document.addEventListener('mousemove', handleMouseMove);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [isMouseTracking, handleMouseMove]);

  // Define handleOverlayClick first before it's used in useEffect
  const handleOverlayClick = useCallback(() => {
    setExtendedTooltip(null);
    setIsMouseTracking(false);
    setFixedHorizontalPosition(null);
    setLastHoveredIndex(null);
  }, []);

  // Add global click listener to close tooltip when clicking anywhere
  useEffect(() => {
    const handleGlobalClick = (event) => {
      if (extendedTooltip) {
        // Check if click is on the chart container or tooltip
        const chartContainer = event.target.closest('[data-chart-container]');
        const tooltipElement = event.target.closest('[data-tooltip-overlay]');
        
        // Only close if click is not on chart container or tooltip
        if (!chartContainer && !tooltipElement) {
          handleOverlayClick();
        }
      }
    };

    if (extendedTooltip) {
      // Add a small delay to prevent immediate closing after opening
      const timer = setTimeout(() => {
        document.addEventListener('click', handleGlobalClick, true);
      }, 100);
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleGlobalClick, true);
      };
    }
  }, [extendedTooltip, handleOverlayClick]);

  // Helper function to show tooltip for data point
  const showTooltipForData = useCallback((dayData, clientX, clientY, dataIndex = null, isHoverUpdate = false) => {
    if (!dayData) {
      return false;
    }
    
    // Check if country_distribution exists and has data
    if (!dayData.country_distribution || !Array.isArray(dayData.country_distribution) || dayData.country_distribution.length === 0) {
      // Create a demo tooltip even without country data
      setExtendedTooltip({
        date: dayData.date,
        sessionCount: dayData.session_count || 0,
        totalTimePlayed: dayData.total_time_played || 0,
        countryData: null // Will show "No geographic data available"
      });
      
      // Only set position if this is not a hover update
      if (!isHoverUpdate) {
        // Simple positioning - always below cursor
        setTooltipPosition({ 
          top: clientY + 30, 
          left: clientX - 200 
        });
        
        setIsMouseTracking(true);
      }
      
      return true;
    }

    const countryData = prepareTooltipCountryData(dayData.country_distribution);
    
    // Only calculate and update position if this is not a hover update
    if (!isHoverUpdate) {
      // Calculate horizontal position based on data point or click position
      const screenWidth = window.innerWidth;
      const tooltipWidth = screenWidth > 1200 ? 700 : screenWidth > 768 ? 500 : 350;
      
      let horizontalPosition = clientX;
      
      // If we have chart reference and data index, calculate position based on chart
      if (chartRef.current && dataIndex !== null && data) {
        try {
          const chart = chartRef.current;
          const chartArea = chart.chartArea;
          const barWidth = chartArea.width / data.length;
          
          // Calculate the center of the bar for this data point
          horizontalPosition = chartArea.left + (dataIndex * barWidth) + (barWidth / 2);
          
          // Convert canvas position to screen position
          const canvasRect = chart.canvas.getBoundingClientRect();
          horizontalPosition += canvasRect.left;
        } catch (error) {
          horizontalPosition = clientX;
        }
      }
      
      // Store the fixed horizontal position
      setFixedHorizontalPosition(horizontalPosition);
      
      // Calculate tooltip position (always below cursor for consistency)
      let left = horizontalPosition - (tooltipWidth / 2); // Center tooltip on data point
      let top = clientY + 30; // Always position below cursor
      
      // Adjust horizontal position if tooltip would go off screen
      if (left + tooltipWidth > screenWidth) {
        left = screenWidth - tooltipWidth - 10;
      }
      if (left < 10) {
        left = 10;
      }
      
      setTooltipPosition({ top, left });
      
      // Enable mouse tracking for vertical following
      setIsMouseTracking(true);
    }
    
    // Always update tooltip content
    setExtendedTooltip({
      date: dayData.date,
      sessionCount: dayData.session_count || 0,
      totalTimePlayed: dayData.total_time_played || 0,
      countryData: countryData
    });
    
    return true;
  }, [data]);

  // Helper function to try alternative element detection
  const tryAlternativeDetection = useCallback((event, chart) => {
    if (!chart) return null;
    
    try {
      // Try nearest mode first
      let elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: false }, false);
      if (elements && elements.length > 0) {
        return elements;
      }
      
      // Try index mode
      elements = chart.getElementsAtEventForMode(event, 'index', { intersect: false }, false);
      if (elements && elements.length > 0) {
        return elements;
      }
    } catch (error) {
      // Fallback silently
    }
    
    return null;
  }, []);

  // Simplified main click handler
  const handleChartClick = useCallback((event, elements, chart) => {
    let clickElements = elements;
    
    // Try alternative detection if no elements
    if (!clickElements || clickElements.length === 0) {
      clickElements = tryAlternativeDetection(event, chart);
    }
    
    if (!validateClickData(data, clickElements)) {
      // Show demo for first data point
      if (data && data.length > 0) {
        const sortedData = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
        const clientX = event?.native?.clientX || event?.clientX || 100;
        const clientY = event?.native?.clientY || event?.clientY || 100;
        showTooltipForData(sortedData[0], clientX, clientY, 0);
      }
      return;
    }

    const sortedData = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
    const dataIndex = clickElements[0].index;
    
    if (dataIndex >= 0 && dataIndex < sortedData.length) {
      showTooltipForData(sortedData[dataIndex], event.native.clientX, event.native.clientY, dataIndex);
    }
  }, [data, tryAlternativeDetection, showTooltipForData]);

  const handleChartHover = useCallback((event, elements) => {
    if (elements.length > 0) {
      if (chartRef.current?.canvas) {
        chartRef.current.canvas.style.cursor = 'pointer';
      }
      
      // If tooltip is already open and we're hovering over a different data point, update it
      if (extendedTooltip && isMouseTracking) {
        const dataIndex = elements[0].index;
        
        // Only update if we're hovering over a different data point
        if (dataIndex !== lastHoveredIndex) {
          setLastHoveredIndex(dataIndex);
          
          const sortedData = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
          
          if (dataIndex >= 0 && dataIndex < sortedData.length) {
            const hoveredData = sortedData[dataIndex];
            
            // Update tooltip content for the new day but keep the same positioning behavior
            showTooltipForData(hoveredData, event.native.clientX, event.native.clientY, dataIndex, true);
          }
        }
      }
    } else {
      if (chartRef.current?.canvas) {
        chartRef.current.canvas.style.cursor = 'default';
      }
      setLastHoveredIndex(null);
    }
  }, [extendedTooltip, isMouseTracking, data, showTooltipForData, lastHoveredIndex]);

  // Additional canvas click handler as fallback
  const handleCanvasClick = useCallback((event) => {
    if (!chartRef.current || !data || data.length === 0) {
      return;
    }
    
    // Try to get elements manually
    try {
      const chart = chartRef.current;
      const rect = chart.canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      // Get elements at the click position
      const elements = chart.getElementsAtEventForMode({
        x: x,
        y: y,
        native: event
      }, 'nearest', { intersect: false }, false);
      
      if (elements && elements.length > 0) {
        // Trigger the main click handler
        handleChartClick({
          native: {
            clientX: event.clientX,
            clientY: event.clientY
          }
        }, elements, chart);
      } else {
        // Calculate approximate data index based on click position
        const chartArea = chart.chartArea;
        const dataIndex = Math.floor(((x - chartArea.left) / chartArea.width) * data.length);
        
        if (dataIndex >= 0 && dataIndex < data.length) {
          const sortedData = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
          if (sortedData[dataIndex] && sortedData[dataIndex].country_distribution) {
            showTooltipForData(sortedData[dataIndex], event.clientX, event.clientY, dataIndex);
          }
        }
      }
    } catch (error) {
      // Fallback silently
    }
  }, [data, handleChartClick, showTooltipForData]);

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Section>
        <ResponsiveTitle>Daily Activity Trends</ResponsiveTitle>
        <p>No daily statistics available for the selected time period</p>
      </Section>
    );
  }

  // Sort data by date to ensure proper chronological order
  const sortedData = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
  const deduplicatedData = sortedData;

  // Prepare chart data with formatted date labels
  const chartData = {
    labels: deduplicatedData.map(item => {
      const date = new Date(item.date);
      const day = date.getDate();
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      return `${dayName} ${month} ${day}`;
    }),
    datasets: [
      {
        type: 'line',
        label: 'Sessions',
        data: deduplicatedData.map(item => item.session_count || 0),
        backgroundColor: 'rgba(33, 150, 243, 0.1)',
        borderColor: 'rgba(33, 150, 243, 0.8)',
        borderWidth: 2,
        tension: 0.4,
        fill: false,
        pointRadius: 0,
        yAxisID: 'y',
      },
      {
        type: 'bar',
        label: 'Total Time Played',
        data: deduplicatedData.map(item => {
          const timeInHours = (item.total_time_played || 0) / 3600;
          return Math.round(timeInHours * 10) / 10; // Round to 1 decimal
        }),
        backgroundColor: deduplicatedData.map(item => 
          isWeekend(item.date) ? 'rgba(156, 39, 176, 0.3)' : 'rgba(76, 175, 80, 0.3)'
        ),
        borderColor: deduplicatedData.map(item => 
          isWeekend(item.date) ? 'rgba(156, 39, 176, 0.6)' : 'rgba(76, 175, 80, 0.6)'
        ),
        borderWidth: 1,
        borderRadius: 4,
        yAxisID: 'y1',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onHover: handleChartHover,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          generateLabels: function(chart) {
            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            
            // Add weekend indicator to legend (non-clickable)
            labels.push({
              text: 'Weekend (Purple)',
              fillStyle: 'rgba(156, 39, 176, 0.3)',
              strokeStyle: 'rgba(156, 39, 176, 0.6)',
              pointStyle: 'rect',
              hidden: false,
              // Make this legend item non-interactive (just informational)
              datasetIndex: -1
            });
            
            return labels;
          }
        },
        onClick: function(e, legendItem, legend) {
          // Only handle clicks for actual datasets (not the weekend indicator)
          if (legendItem.datasetIndex !== undefined && legendItem.datasetIndex >= 0) {
            const index = legendItem.datasetIndex;
            const chart = legend.chart;
            
            if (chart.isDatasetVisible(index)) {
              chart.hide(index);
              legendItem.hidden = true;
            } else {
              chart.show(index);
              legendItem.hidden = false;
            }
            
            chart.update();
          }
        },
        onHover: function(e, legendItem) {
          // Change cursor to pointer for clickable legend items
          if (legendItem.datasetIndex !== undefined && legendItem.datasetIndex >= 0) {
            e.native.target.style.cursor = 'pointer';
          } else {
            e.native.target.style.cursor = 'default';
          }
        },
        onLeave: function(e) {
          e.native.target.style.cursor = 'default';
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        cornerRadius: 8,
        callbacks: {
          title: function(context) {
            const dataIndex = context[0].dataIndex;
            const date = new Date(deduplicatedData[dataIndex].date);
            const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
            const formattedDate = date.toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric',
              year: 'numeric'
            });
            const weekendLabel = isWeekend(deduplicatedData[dataIndex].date) ? ' (Weekend)' : '';
            return `${dayName}, ${formattedDate}${weekendLabel}`;
          },
          label: function(context) {
            const datasetLabel = context.dataset.label || '';
            const value = context.parsed.y;
            
            if (datasetLabel.includes('Time Played')) {
              return `${datasetLabel}: ${value.toFixed(1)}h`;
            }
            
            return `${datasetLabel}: ${value.toLocaleString()}`;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 10,
          },
        },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: '#666',
          callback: function(value) {
            return Math.round(value).toLocaleString();
          }
        },
        title: {
          display: true,
          text: 'Sessions',
          color: '#666',
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: '#666',
          callback: function(value) {
            return Math.round(value) + 'h';
          }
        },
        title: {
          display: true,
          text: 'Total Time Played (hours)',
          color: '#666',
        },
      },
    },
  };

  return (
    <Section>
      <ResponsiveTitle>Daily Activity Trends</ResponsiveTitle>
      <p style={{ margin: '0 0 15px 0', color: '#666', fontSize: '0.9rem', fontStyle: 'italic' }}>
        💡 Click on any data point to see detailed country distribution
      </p>
      <ChartWrapper>
        <ChartContainer 
          onClick={handleCanvasClick}
          style={{ cursor: 'pointer' }}
          data-chart-container="true"
        >
          <Bar 
            ref={chartRef} 
            data={chartData} 
            options={chartOptions}
            onClick={handleChartClick}
          />
        </ChartContainer>
        
        {/* Extended Tooltip Overlay */}
        <TooltipOverlay 
          tooltipData={extendedTooltip}
          position={tooltipPosition}
          onClose={handleOverlayClick}
        />
      </ChartWrapper>
    </Section>
  );
};

DailyStats.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      session_count: PropTypes.number,
      total_time_played: PropTypes.number,
      total_blocks_advanced: PropTypes.number,
      country_distribution: PropTypes.array,
    })
  ),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
};

DailyStats.displayName = 'DailyStats';

export default DailyStats;
